import { apiClient, isApiError } from '@/services/api/client'
import type { ClusterWorkloadStatusResponse } from '@/features/managed-clusters/types'

// Deployment modes for clusters
export type DeploymentMode = 'platform_managed_shared' | 'platform_managed_dedicated' | 'byok'

// Cluster status values
/** Known `deletion_paused_reason` codes. Open-ended on purpose: unknown codes render nothing. */
export type DeletionPausedReason = 'legal_hold'

export type ClusterStatus =
  | 'requested'
  | 'provisioning'
  | 'pending_agent'
  | 'agent_connecting'
  | 'active'
  | 'degraded'
  | 'suspended'
  | 'agent_offline'
  | 'deleting'
  | 'deleted'
  | 'failed'

// Agent status values. Go: cluster.AgentStatus (internal/domain/cluster/agent.go:20-26).
export type AgentStatus =
  | 'pending'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'unhealthy'
  | 'error'
  | 'decommissioned'

// Engine types
/**
 * Workflow engines a cluster agent can run: the single source for the EngineType
 * union, the create forms' zod enums and the install-command builder (which
 * refuses any other engine name).
 */
export const CLUSTER_ENGINES = ['langflow', 'agentic', 'airflow', 'dagster'] as const
export type EngineType = (typeof CLUSTER_ENGINES)[number]

// Ollama GPU configuration
export interface OllamaGPUConfig {
  enabled: boolean
  count?: number
}

// Ollama LLM inference server configuration
export interface OllamaConfig {
  enabled: boolean
  image?: string
  models?: string[]
  disk_size?: string
  gpu?: OllamaGPUConfig
  concurrent_requests?: number
  keep_alive?: string
}

// Queue configuration for engine scaling
export interface QueueConfig {
  enabled: boolean
  worker_replicas: number
  hpa_enabled?: boolean
  hpa_min_replicas?: number
  hpa_max_replicas?: number
  hpa_target_cpu_percent?: number
  // Job delivery settings — persisted by the platform, applied via agent heartbeat
  delivery_preference?: string   // "auto" | "reliable" | "low_latency"
  poll_interval_seconds?: number // 5–300
  // Langflow queue mode (Celery workers + Redis + PostgreSQL)
  langflow_enabled?: boolean
  langflow_worker_replicas?: number
  langflow_hpa_enabled?: boolean
  langflow_hpa_min_replicas?: number
  langflow_hpa_max_replicas?: number
  langflow_hpa_target_cpu_percent?: number
  // Agentic engine (controller/worker split)
  agentic_enabled?: boolean
  agentic_mode?: 'all' | 'split'
  agentic_worker_replicas?: number
  agentic_hpa_enabled?: boolean
  agentic_hpa_min_replicas?: number
  agentic_hpa_max_replicas?: number
  agentic_hpa_target_cpu_percent?: number
  /**
   * P2.77 — tenant-enabled job output sink directive (`*bool` omitempty on
   * the backend). `undefined`/absent means never explicitly set on this
   * cluster (the platform default applies server-side); `true`/`false` is an
   * explicit admin choice. Persisted/read through the same cluster config
   * update path as `delivery_preference`/`poll_interval_seconds`. As of this
   * writing the backend directive field had not yet landed (tracked
   * separately, P2.77 backend) — this frontend codes against the documented
   * contract shape.
   */
  job_output_sink_enabled?: boolean
}

// Monitoring configuration for BYOK clusters
export interface MonitoringConfig {
  cpu_warning_threshold: number
  memory_warning_threshold_gb: number
  job_queue_depth_alert: number
  heartbeat_staleness_seconds: number
  execution_timeout_seconds: number
  sla_availability_target: number
  agent_log_level: string
  /**
   * P2.489: consecutive heartbeats reporting this cluster's own K8s
   * apiserver unreachable before it's marked degraded. 0/undefined means
   * "use the server default" — see
   * DefaultApiserverUnreachableThreshold in
   * internal/domain/cluster/heartbeat_reconciler.go.
   */
  apiserver_unreachable_threshold?: number
  /**
   * P2.489 review fix #5 (recovery hysteresis): consecutive heartbeats
   * reporting the apiserver reachable again before a degraded cluster
   * recovers to active. Symmetric to apiserver_unreachable_threshold;
   * 0/undefined means "use the server default" — see
   * DefaultApiserverRecoveryThreshold in
   * internal/domain/cluster/heartbeat_reconciler.go.
   */
  apiserver_recovery_threshold?: number
  /**
   * Issue #273: seconds a DISABLED engine/service may keep running before
   * Cluster Workloads flips it from "Removing" to "Removal stuck". 0/undefined
   * means the server default (DefaultRemovalStuckAfter, 5 min) - see
   * internal/domain/cluster/entity.go.
   */
  removal_stuck_after_seconds?: number
}

// Connectivity status reported by the agent
export interface ConnectivityStatus {
  /** Agent-reported confirmed mode: "http_poll" | "kafka_direct" | "kafka_bridge" | "unknown" (H38). */
  delivery_mode: string
  /** Platform's requested mode (domain.ResolveKafkaDirective decision): "http_poll" | "kafka_bridge" (H38). */
  requested_mode?: string
  agent_online: boolean
  agent_last_seen: string | null
  agent_version: string
  kafka_connectivity: string
  redis_status: string
  worker_ready: number
  worker_desired: number
  queued_jobs: number
  delivery_preference: string   // "auto" | "reliable" | "low_latency"
  poll_interval_seconds: number
}

// Platform services configuration (infrastructure services consumed by engines)
export interface PlatformServicesConfig {
  ollama?: OllamaConfig
  // Future: chromadb, qdrant, pgvector, vllm, milvus, triton
}

// Cluster resources configuration
export interface ClusterResources {
  cpu_cores?: number
  memory_gb?: number
  storage_gb?: number
  max_workers?: number
  node_count?: number
  vm_size?: string
}

// Cluster entity
export interface Cluster {
  id: string
  tenant_id: string
  name: string
  display_name?: string
  deployment_mode: DeploymentMode
  region?: string
  tier?: string
  /**
   * The environment this cluster is in (at most one; ADR-347), kept in step with the bindings by the
   * server: read-only here, and PATCH /clusters/{id}/environment refuses changes. Bind it on
   * Infrastructure > Environments. Null when it is in none. The Log Archive screens group
   * by it. Render as a plain text chip, never assume a closed enum.
   */
  environment?: string | null
  resources: ClusterResources
  engines: EngineType[]
  queue_config?: QueueConfig
  platform_services?: PlatformServicesConfig
  monitoring_config?: MonitoringConfig
  status: ClusterStatus
  status_reason?: string
  /**
   * Machine-readable reason a delete in progress is paused, derived server-side
   * (Go: cluster.Cluster.DeletionPausedReason, JSON `deletion_paused_reason`,
   * internal/domain/cluster/entity.go). Absent when the delete is not paused.
   * Branch on this, never on the human `status_reason` text.
   */
  deletion_paused_reason?: DeletionPausedReason
  api_server_url?: string
  namespace?: string
  created_by?: string
  created_at: string
  updated_at: string
  deletion_requested_at?: string
  force_deleted?: boolean
  /**
   * Reversible "Parked" state (Infrastructure > Clusters Archived filter) --
   * independent of `status` and of soft-delete. `undefined`/absent means not
   * archived. Set together by the backend on archive/unarchive (migration
   * 000980) -- see docs/design/DELETED-TRASH-SECTION-DESIGN.md's clusters
   * paragraph for the full Archive vs Delete semantics.
   */
  archived_at?: string
  archived_by?: string
  console_url?: string
  langflow_console_url?: string
  airflow_console_url?: string
  dagster_console_url?: string
  airflow_spec?: AirflowEngineSpec
  dagster_spec?: DagsterEngineSpec
  byok_ca_cert_pem?: string
  /**
   * Agent RBAC self-check result (P2.25) — null until the agent's first
   * self-check heartbeat lands (no K8s client yet, or a pre-upgrade agent
   * image that doesn't run the check at all). See AgentCapabilityCheck doc
   * comment.
   */
  agent_capabilities?: AgentCapabilityCheck | null
  /** BYOK only: worst-of agent upgrade status with a per-agent breakdown (Phase 1, display only). */
  agent_upgrade?: ClusterAgentUpgrade
}

// Agent entity
export interface Agent {
  id: string
  cluster_id: string
  // Go: cluster.Agent.TenantID *uuid.UUID `json:"tenant_id"` (internal/domain/cluster/agent.go:98) —
  // nullable (a platform-scoped agent with no tenant binding serializes this as null).
  tenant_id: string | null
  agent_name: string
  agent_version?: string
  agent_ip?: string
  kubernetes_version?: string
  status: AgentStatus
  status_reason?: string
  capabilities?: AgentCapabilities
  metrics?: AgentMetrics
  /** Which deployment process this is: all | controller | worker (cluster.AgentRole). */
  role?: string
  // Go: cluster.Agent.LastHeartbeatAt *time.Time `json:"last_heartbeat_at,omitempty"`
  // (internal/domain/cluster/agent.go:142).
  last_heartbeat_at?: string
  registered_at: string
  // A2A (ADR-128 / A2A-BUILD-PLAN-AUG30.md Phase 1a) — set by THIS agent's
  // own heartbeat when VHARTA_A2A_ENABLED=true on that process
  // (internal/api/v1/cluster/agent_handler.go's Heartbeat). Read-through
  // cache; agents/clusters is the source of truth, not a second registry.
  a2a_enabled?: boolean
  a2a_endpoint?: string
  /**
   * Agent upgrade status (Phase 1, display only). Computed by the backend
   * (internal/domain/agentupgrade.AgentSummary) from this agent's reported
   * version, the chart appVersion the platform serves and the platform
   * minimum. Absent on platform-managed clusters, decommissioned agents and
   * older gateways. Never compare versions in the UI.
   */
  upgrade?: AgentUpgradeStatus
}

/**
 * internal/domain/agentupgrade.ReleaseInfo: signed-release provenance of a reported agent version, derived by the gateway from its
 * embedded signed release manifests. DISPLAY ONLY (nothing is blocked on it).
 */
export interface ReleaseInfo {
  /** verified | unsigned | failed | expired | yanked | not_configured | unknown */
  state: string
  /** Closed reason code for failed/yanked. */
  reason?: string
  sequence?: number
}

/** internal/domain/cluster.AgentReleaseManifest: the AGENT's own self-report of its release-manifest check (advisory, default off). */
export interface AgentReleaseManifestReport {
  /** verified | unsigned | failed | expired | yanked | unknown | not_configured */
  state: string
  reason?: string
  version?: string
  sequence?: number
  checked_at?: number
}

/** internal/domain/agentupgrade.State */
export type AgentUpgradeState = 'ok' | 'outdated' | 'unsupported' | 'unknown' | 'ahead'

// ---------------------------------------------------------------------------------------------------------------------------------
// One-click agent upgrade (ADR-303). Go: internal/domain/agentupgrade/request.go, internal/api/v1/cluster/agent_upgrade_request_handler.go.
// ---------------------------------------------------------------------------------------------------------------------------------

/** The closed set of refusal / unavailability codes (agentupgrade.KnownRefusalReasons). Unknown codes render generic copy. */
export type AgentUpgradeReasonCode =
  | 'cluster_not_byok'
  | 'no_agent'
  | 'agent_lacks_capability'
  | 'not_opted_in'
  | 'upgrades_paused'
  | 'policy_unavailable'
  | 'target_unknown'
  | 'target_yanked'
  | 'target_not_newer'
  | 'current_version_unknown'
  | 'below_min_upgrade_from'
  | 'cluster_layer_changed'
  | 'feature_unknown'
  | 'feature_not_offered'
  | 'feature_already_on'
  | 'upgrade_in_flight'
  | 'nothing_to_do'
  | 'forbidden'
  | 'feature_consent_missing'
  | 'rollback_cooldown'
  | 'request_rate_limited'
  | 'request_id_invalid'
  | 'releases_not_fresh'
  | 'request_id_conflict'

/** The only feature switch in v1 (carried in the signed release manifest). */
export const FEATURE_TENANT_RAG_VECTOR_STORE = 'tenant_rag_vector_store'

export type AgentUpgradeRequestStatus = 'requested' | 'in_progress' | 'succeeded' | 'failed' | 'rolled_back' | 'expired'

/** agentupgrade.Record as the console sees it (upgradeRecordDTO). */
export interface AgentUpgradeRequestRecord {
  request_id: string
  cluster_id: string
  status: AgentUpgradeRequestStatus
  /** Closed wire reason of a failed / rolled_back outcome (internal/agentupgrade/wire KnownReasons). */
  reason?: string
  from_version: string
  to_version: string
  manifest_sha256: string
  features: string[]
  requested_by: string
  requested_at: string
  expires_at: string
  updated_at: string
}

export interface AgentUpgradeTarget {
  version: string
  manifest_sha256: string
  sequence: number
  features?: string[]
  /** The upgrader image this release binds; the cluster-layer command sets upgrader.image.digest to it. */
  upgrader_image_digest?: string
}

export interface AgentVectorStoreOffer {
  /** Tenant RAG is on and the agent reports vector_store_not_configured. */
  needed: boolean
  /** needed, the cluster is eligible and a signed release offers the switch. */
  available: boolean
  reason?: AgentUpgradeReasonCode | string
  version?: string
  manifest_sha256?: string
  /** The vector-store image the offering release pins (sha256:<64 hex>); the copy-paste command pins it too. */
  image_digest?: string
}

/**
 * The repository the upgrader image is published to. Deliberately nothing else (round 5, H1): the cosign key the customer verifies with is
 * their LOCAL cosign.pub, obtained out of band; the control plane never names a key.
 */
export interface UpgraderTrust {
  repository: string
}

/**
 * internal/domain/agentupgrade.ChartTrust (round 6, D1/D2): the STRUCTURED facts the console builds every verified Helm command from.
 * `signed` is true only while the served chart carries a cosign bundle and the root-signed key set names a publishing key; the
 * fingerprint is `sha256sum cosign.pub` of the correct key, for the customer to compare with the file they hold. Never command text.
 */
export interface ChartTrust {
  signed: boolean
  chart_version?: string
  chart_sha256?: string
  publish_key_id?: string
  publish_key_fingerprint?: string
  key_set_version?: number
}

/** GET /tenant/clusters/{id}/agent-upgrade */
export interface AgentOneClickView {
  /** "Upgrade now" may be offered: capable, opted-in, not paused, and a newer signed release can be applied. */
  available: boolean
  reason?: AgentUpgradeReasonCode | string
  paused_reason?: string
  target?: AgentUpgradeTarget
  target_version?: string
  /** Upgrader image digest of the newest release when it is refused for cluster_layer_changed (for the administrator command). */
  target_upgrader_image_digest?: string
  /** Where the customer's `cosign verify` of the upgrader image looks (the repository; deployment configuration). */
  upgrader_trust?: UpgraderTrust
  vector_store: AgentVectorStoreOffer
  /** The served chart's signing state and publishing key (round 6). The console builds every command from these; it never renders server text. */
  chart_trust?: ChartTrust
  current?: AgentUpgradeRequestRecord
}

/** POST /tenant/clusters/{id}/agent-upgrade. Names ONE signed release by its manifest hash; nothing else is accepted. */
export interface AgentUpgradeRequestBody {
  target_manifest_sha256: string
  feature_ids?: string[]
  request_id?: string
}

/**
 * A declined request: the closed code, the fixed message and, for cluster_layer_changed, the STRUCTURED facts the console builds the
 * administrator command from (the upgrader image digest and the repository). No command text is read from a response (round 6, HIGH-1).
 */
export interface AgentUpgradeRefusal {
  code: AgentUpgradeReasonCode | string
  message: string
  targetVersion?: string
  pausedReason?: string
  targetUpgraderImageDigest?: string
  upgraderTrust?: UpgraderTrust
}

const UPGRADER_DIGEST_RE = /^sha256:[0-9a-f]{64}$/

/** The refusal behind a failed POST, or null when the error is not one (network, 5xx without a code). */
export function agentUpgradeRefusalFromError(err: unknown): AgentUpgradeRefusal | null {
  if (!isApiError(err) || !err.code) return null
  const body = (err.response?.data ?? {}) as {
    target_version?: string
    paused_reason?: string
    target_upgrader_image_digest?: string
    upgrader_trust?: { repository?: string }
  }
  const digest = typeof body.target_upgrader_image_digest === 'string' && UPGRADER_DIGEST_RE.test(body.target_upgrader_image_digest) ? body.target_upgrader_image_digest : undefined
  const repository = typeof body.upgrader_trust?.repository === 'string' ? body.upgrader_trust.repository : undefined
  return {
    code: err.code,
    message: err.message,
    targetVersion: typeof body.target_version === 'string' ? body.target_version : undefined,
    pausedReason: typeof body.paused_reason === 'string' ? body.paused_reason : undefined,
    targetUpgraderImageDigest: digest,
    upgraderTrust: repository ? { repository } : undefined,
  }
}

/** internal/domain/agentupgrade.AgentStatus */
export interface AgentUpgradeStatus {
  state: AgentUpgradeState
  reported_version: string
  served_version: string
  min_supported_version?: string
  upgrades_paused: boolean
  paused_reason?: string
  /** Behind (outdated/unsupported) and not paused. The UI also requires owner/admin. */
  upgrade_command_available: boolean
  /** Signed-release provenance of reported_version (absent when the gateway has no release reader). */
  release?: ReleaseInfo
}

/** internal/domain/agentupgrade.AgentRow */
export interface AgentUpgradeRow {
  agent_id: string
  agent_name?: string
  role?: string
  version: string
  state: AgentUpgradeState
  connection?: string
  release?: ReleaseInfo
}

/** GET /tenant/clusters/agent-upgrade-summary */
export interface AgentUpgradeSummary {
  clusters_below: number
  first_cluster_id?: string
  first_cluster_name?: string
  cluster_ids: string[]
  upgrades_paused: boolean
  paused_reason?: string
}

/** internal/domain/agentupgrade.ClusterStatus: worst of the cluster's agents. */
export interface ClusterAgentUpgrade {
  state: AgentUpgradeState
  served_version: string
  min_supported_version?: string
  minimum_clamped?: boolean
  upgrades_paused: boolean
  paused_reason?: string
  upgrade_command_available: boolean
  platform_url?: string
  /** The served chart's signing state and publishing key (round 6, D1/D2), for the verified upgrade command. */
  chart_trust?: ChartTrust
  mixed_versions: boolean
  versions: string[]
  counts: Partial<Record<AgentUpgradeState, number>>
  agents: AgentUpgradeRow[]
  /** Worst signed-release provenance among the agents. */
  release?: ReleaseInfo
}

// A2A advertised skill — merged into capabilities.a2a_skills by the same
// heartbeat write that already sends {"engines": [...]} (Phase 1a; no new
// column, capabilities JSONB carries it).
export interface A2ASkill {
  id: string
  name: string
  description?: string
  input_schema?: Record<string, unknown>
  output_schema?: Record<string, unknown>
  risk_class?: string
}

// Agent capabilities
export interface AgentCapabilities {
  engines?: string[]
  max_concurrent_jobs?: number
  supports_gpu?: boolean
  a2a_skills?: A2ASkill[]
  /**
   * Present (closed=true) while this agent refuses every private destination (Ollama,
   * on-prem Git, private probes) because it cannot yet discover its cluster's node and
   * API-server addresses. It retries and reopens by itself; reason is one of
   * discovery_pending | discovery_failed | discovery_stale | no_kubernetes_client.
   */
  egress_private_tier?: { closed: boolean; reason?: string }
  /**
   * H31 — true when this connected agent's binary understands the H19
   * generic agent-settings heartbeat directive block and applies
   * desired-state settings (e.g. A2A enable/disable) on its next
   * heartbeat. Absent (undefined) on a pre-H31 agent — same "field never
   * existed on that build" reading as engines/supports_gpu on the oldest
   * agents. See AgentSettingsCard.tsx's "Requires agent upgrade" state.
   */
  agent_settings_supported?: boolean
  /**
   * True while this agent process runs the cluster add-on manager and therefore reports
   * `metrics.cluster_addons` (capability cluster_addons_v1). Absent on an agent built before
   * add-on reporting existed: the console then asks for an upgrade.
   */
  cluster_addons_supported?: boolean
  /** True while tenant RAG is wired and on for this agent process (capability tenant_rag_v1). */
  tenant_rag_supported?: boolean
  /** Capability kb_key_host_policy_v1: this agent enforces the embedding key host policy at dial time. Absent on an older agent. */
  kb_key_host_policy_supported?: boolean
  /** Capability kb_retention_sweep_v1: this agent deletes indexed chunks older than the knowledge base's retention. Absent on an older agent. */
  kb_retention_sweep_supported?: boolean
  /** Capability kb_retention_sweep_job_v1: this agent handles the dedicated daily retention sweep job. Absent on an older agent. */
  kb_retention_sweep_job_supported?: boolean
  /**
   * H56 — GENERIC per-descriptor applied-state map, keyed by
   * `AgentSettingDescriptor.key` (e.g. "a2a", "profile_sync_key"). The ONE
   * mechanism every registered descriptor's applied/convergence state
   * reaches this frontend through, used by deriveAgentSettingConvergence
   * (AgentSettingsCard.tsx) as a fallback when a descriptor has no
   * `reported_field` of its own (today: profile_sync_key — see
   * AgentSettingDescriptor.reported_field's doc comment). Absent on a
   * pre-0.6.8 agent binary or before its first post-upgrade heartbeat.
   */
  applied_agent_settings?: Record<string, AppliedAgentSetting>
}

/**
 * H56 — one entry in AgentCapabilities.applied_agent_settings. Mirrors
 * Go's domain.AppliedAgentSetting (internal/domain/cluster/agent.go).
 */
export interface AppliedAgentSetting {
  applied: boolean
  /** Descriptor-specific version/generation (e.g. profile_sync_key's Vault KV version); 0/absent when the descriptor has none (e.g. a2a). */
  version?: number
  /** Agent-supplied short code for why a desired-on setting is not applied (e.g. "control_plane_not_https"). Display only via known-code copy. */
  reason?: string
  /** Agent-supplied code for where the setting's credentials come from (tenant_rag: vault | mounted | delivered). Additive: absent on an older agent. Display only via known-value copy. */
  source?: string
  /** Agent-supplied display string in a shape fixed per key (gateway-validated): tenant_rag_vector_store_egress "<ns>/<svc>:<port>", tenant_rag a dial class (dial_timeout, dial_refused, dial_dns, dial_tls, dial_auth, dial_other). */
  detail?: string
}

/**
 * One kind of cluster's CPU/memory use. Go: cluster.ResourceUsage (internal/domain/cluster/resource_usage.go).
 * `total_clusters > 0` with `agents === 0` means present but not reporting, which is not 0% use.
 */
export interface ClusterResourceUsage {
  cpu: number
  memory: number
  total_clusters: number
  clusters: number
  agents: number
}

export interface TenantResourceUsage {
  byok: ClusterResourceUsage
  managed: ClusterResourceUsage
}

// Agent metrics from heartbeat
export interface AgentMetrics {
  cpu_available?: number
  memory_available_gb?: number
  active_jobs?: number
  queued_jobs?: number
  // Cluster resource snapshot (BYOK only)
  node_count?: number
  allocatable_cpu_cores?: number
  allocatable_memory_gb?: number
  running_pods?: number
  pending_pods?: number
  engine_pods?: EnginePodStatus[]
  /** Platform-managed cluster add-ons (metrics-server first), keyed by add-on name. */
  cluster_addons?: Record<string, ClusterAddOnStatus>
  /** The agent's advisory self-report of its signed-release check; absent when the check is off (the default). */
  release_manifest?: AgentReleaseManifestReport
}

/**
 * One cluster add-on's agent-reported status. `state` and `reason` come from
 * closed allowlists on the server (internal/domain/cluster/cluster_addons.go);
 * the console maps them to copy in utils/clusterAddOns.ts.
 */
export interface ClusterAddOnStatus {
  /** provided_by_cluster | installed | installing | failed | off */
  state: string
  reason?: string
  chart_version?: string
  /** The operator explicitly disabled the admission policies that bound the add-on's grants. */
  admission_opt_out?: boolean
}

// Per-engine pod health reported by BYOK agent
export interface EnginePodStatus {
  engine: string
  ready_replicas: number
  total_replicas: number
  phase: string // "Running" | "Pending" | "CrashLoopBackOff" | "Unknown"
  restart_count: number
  last_error?: string
}

// Cluster health summary
export interface ClusterHealth {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown'
  score: number // 0-100
  online_agents: number
  total_agents: number
  total_active_jobs: number
  total_queued_jobs: number
  avg_cpu_available: number
  avg_memory_available_gb: number
  last_heartbeat?: string
  issues: string[]
}

// Agent RBAC self-check (P2.25, tracker row) — the controller performs a
// SelfSubjectAccessReview for every verb it needs and reports gaps back to
// the control plane, so a tenant admin can see and fix missing cluster RBAC
// entirely from the UI (no kubectl needed to DIAGNOSE — applying the fixed
// manifest is still the tenant's own kubectl apply, since Vharta is never
// granted RBAC-granting permissions on a BYOK cluster itself).
//
// Contract frozen by agent w8-p2-25-backend (wave8-p2-25-contract.md §2,
// 2026-09-09). `namespace` is "" for a cluster-scoped check (e.g.
// Namespaces, ClusterRoleBindings) — never omitted. `checked_at` can itself
// be null (distinct from the whole object being null/absent — see
// `Cluster.agent_capabilities` below).
export interface AgentCapabilityGap {
  resource: string
  verb: string
  namespace: string
  /**
   * Plain-English description computed server-side (H52,
   * AgentCapabilityGap.Describe in internal/domain/cluster/entity.go) —
   * when `role` (below) is set, this is already prefixed
   * "worker: cannot list secrets in the vharta-connectors namespace" style.
   * Absent on a pre-H52 control plane response.
   */
  description?: string
  /**
   * H52 — which agent process this gap applies to ("all" | "controller" |
   * "worker"), populated only on a gap produced by the cluster-level merge
   * across a split-mode cluster's live agents (MergeAgentRBACReports).
   * Absent for a single-agent ("all" mode) cluster, where every gap
   * necessarily applies to the one process running.
   */
  role?: string
  /** H52 — id of the agent row that reported this gap (merge output only). */
  agent_id?: string
}

export interface AgentCapabilityCheck {
  checked_at: string | null
  all_ok: boolean
  /** Always [] (never null/omitted) when all_ok is true. */
  missing: AgentCapabilityGap[]
  /**
   * P2.77 — job output sink capability state, one of "ready" | "disabled" |
   * "error:<reason>", reported by the agent on the same heartbeat/self-check
   * payload as `all_ok`/`missing` above (same `clusters.agent_capabilities`
   * JSON column). Absent/null until the agent's next heartbeat reports it —
   * render as Pending, never inferred from the `job_output_sink_enabled`
   * directive. As of this writing the backend had not yet added this field
   * (tracked separately, P2.77 backend); this frontend codes against the
   * documented contract shape.
   */
  job_output_sink?: string | null
}

// Cluster event
export interface ClusterEvent {
  id: string
  cluster_id: string
  tenant_id: string
  event_type: string
  severity: 'info' | 'warning' | 'error' | 'critical'
  message: string
  details?: Record<string, string>
  actor_type?: 'user' | 'system' | 'agent'
  actor_id?: string
  created_at: string
}

// Create cluster request
export interface CreateClusterRequest {
  name: string
  display_name?: string
  deployment_mode: DeploymentMode
  region?: string
  tier?: string
  resources?: ClusterResources
  engines?: EngineType[]
  // BYOK specific
  api_server_url?: string
  namespace?: string
}

// Create cluster response with token for BYOK
export interface CreateClusterResponse {
  success: boolean
  data: {
    cluster: Cluster
    agent_token?: string
    token_expires_at?: string
    /** Address the agent must call; absent on older gateways. */
    platform_url?: string
    /** The served chart's signing state (round 6, D1): with it the install command verifies the chart first. */
    chart_trust?: ChartTrust
  }
}

// Delivery preference update payload — sent by ConnectivityPanel
export interface UpdateDeliveryPreferenceRequest {
  delivery_preference: string   // "auto" | "reliable" | "low_latency"
  poll_interval_seconds: number // 5–300
}

// H19 — generic per-cluster "Agent settings" (config over code: A2A is the
// first descriptor, not a special case). One descriptor-driven resource
// instead of a dedicated a2a endpoint, so a future agent-directed setting
// needs no new endpoint or component — only a new
// domain.AgentSettingDescriptor entry in the backend's
// AgentSettingsRegistry (internal/domain/cluster/agent_settings_registry.go).
//
// Contract verified against origin/feat/a2a-enable-from-console @ 4691d1ba8
// (internal/api/v1/cluster/agent_settings_handler.go): GET and PUT both use
// the repo-wide `Response{success, data, error}` envelope like every other
// cluster endpoint (ClusterResponse etc.) — NOT a bare top-level body.
// PUT's body is a partial merge patch keyed by descriptor `key`, e.g.
// `{"a2a": {"enabled": true}}`; a key whose role_tier the caller doesn't
// satisfy 403s the WHOLE request before any write (all-or-nothing, never a
// partial apply). PUT's response `data` is the SAME shape as GET's plus
// `changed_keys` (present only on PUT, `[]` on a no-op patch) — the
// backend's own doc comment says the frontend may replace its cached
// settings from either response without a refetch, though this frontend
// still invalidates-and-refetches (useUpdateAgentSettings) rather than
// wiring that shortcut.
//
// There is no `type` field on the wire (agentSettingDescriptorDTO has none)
// — every descriptor is a boolean toggle today, the only shape
// AgentSettingValue supports; AgentSettingsCard renders a Switch for all of
// them unconditionally rather than gating on a discriminant that doesn't exist.

/**
 * Which roles may change a row. Mirrors the platform's two existing tiers —
 * "owner" is tenant_owner + platform_admin (the outbound kill switch's
 * CAN_TRIP_ROLES; A2A ships as this tier per the 2026-09-20 security
 * review — confirmed backend-side by
 * callerSatisfiesAgentSettingRoleTier/AgentSettingRoleTierOwner), "admin" is
 * tenant_owner + tenant_admin + platform_admin (ClusterDetail's canDelete
 * tier). Wire values are exactly "owner"/"admin" (backend-pinned by
 * agent_settings_handler_test.go). An unrecognized value is treated as
 * "owner" (fail closed) by ROLE_TIER_ALLOWED_ROLES in AgentSettingsCard.tsx.
 */
export type AgentSettingRoleTier = 'owner' | 'admin'

export interface AgentSettingDescriptor {
  key: string
  title: string
  description: string
  role_tier: AgentSettingRoleTier
  /** True when enabling delivers secret material (e.g. A2A's signing key) the UI never sees or displays. */
  requires_secret: boolean
  /**
   * The field name on `Agent` (clusterApi.ts) this setting's REPORTED state
   * comes from, e.g. `"a2a_enabled"` — read dynamically by
   * deriveAgentSettingConvergence, so a new descriptor needs no new frontend
   * code as long as its reported field already exists on the heartbeat.
   * `omitempty` on the wire; optional here defensively — as of H56, a
   * descriptor with NO `reported_field` (e.g. profile_sync_key) is not
   * unsupported: deriveAgentSettingConvergence (via `reportedApplied`) falls
   * back to the GENERIC `capabilities.applied_agent_settings[key]` map for
   * it, so a new descriptor never needs a bespoke top-level field just to
   * converge in the UI.
   */
  reported_field?: string
  /**
   * H35 — WHY the last heartbeat's directive build omitted this key's
   * fragment despite `desired[key].enabled === true`, e.g.
   * `"signing_key_source_unwired"` or `"no_kex_key_this_heartbeat"` (see
   * domain.AgentSettingOmitReason, internal/domain/cluster/
   * agent_settings_registry.go — not a closed enum, a documented string).
   * `omitempty` on the wire: absent whenever nothing is recorded (never
   * omitted, already converged/cleared, or the observability store isn't
   * wired) — AgentSettingsCard falls back to the generic "waiting for
   * agent heartbeat" text in that case. Reason strings this frontend
   * doesn't recognize fall back to that same generic text rather than
   * rendering the raw key.
   */
  omit_reason?: string
  /** ISO timestamp the above reason was last recorded, when present. */
  omit_reason_at?: string
  /**
   * H61 (ADR-186 dated amendment) — optional helper copy shown ONLY once a
   * desired-disabled row has actually converged to the "Off" state, for a
   * descriptor whose disable is honestly not instantaneous (e.g.
   * profile_sync_key: the agent keeps its current key deriver running
   * until it restarts — see domain.AgentSettingDescriptor.DisabledHint's
   * doc comment, internal/domain/cluster/agent_settings_registry.go).
   * `omitempty` on the wire; a descriptor with nothing extra to say (e.g.
   * a2a) simply omits it and the card shows no additional hint.
   */
  disabled_hint?: string
  /**
   * True when the agent reads this setting only at process boot: saving it does
   * not converge on a heartbeat. The card then shows "Saved — takes effect after
   * the agent restarts" plus `restart_instruction` instead of a heartbeat promise.
   */
  requires_restart?: boolean
  restart_instruction?: string
  /**
   * In-app path where this setting is managed in one place across clusters
   * (e.g. the Airflow task-log archive setting -> Infrastructure > Archival).
   * `omitempty` on the wire. The card shows a "Managed in ..." link when set;
   * the setting stays editable on the card (one store, two entry points).
   */
  home_link?: string
  /**
   * True for a setting the control plane evaluates itself (for example at save
   * time): nothing is sent to the agent and the agent never reports it applied,
   * so the card shows no heartbeat convergence badge for it.
   */
  control_plane_only?: boolean
  /**
   * "int_list" = a list of whole numbers under `value_field`; "string" = one text
   * value under `value_field`; absent = the {enabled} switch.
   */
  value_kind?: string
  value_field?: string
  /** Shown when nothing is stored yet. */
  value_default?: number[] | string
}

/**
 * Tenant-level default of the per-cluster airflow_task_log_archive setting
 * (GET/PUT /tenant/clusters/task-log-archive-default). `explicit` is false when
 * the tenant never set one (platform default, ON).
 */
export interface TaskLogArchiveDefault {
  enabled: boolean
  explicit: boolean
}

export interface TaskLogArchiveApplyResult {
  cluster_id: string
  cluster_name: string
  changed: boolean
  error?: string
}

export interface TaskLogArchiveApplyData {
  default: TaskLogArchiveDefault
  results: TaskLogArchiveApplyResult[] | null
}

/**
 * A toggle descriptor stores {enabled}; an "int_list" descriptor stores its list
 * under the descriptor's `value_field` (for example {ports: [443]}); a "string"
 * descriptor stores its text there (for example {registry: "ghcr.io/org"}).
 */
export interface AgentSettingValue {
  enabled?: boolean
  ports?: number[]
  /** "string" descriptors: the container registry host for packaged MCP connectors. */
  registry?: string
}

/** The payload under `data` for both GET and PUT .../agent-settings. */
export interface AgentSettingsData {
  descriptors: AgentSettingDescriptor[]
  desired: Record<string, AgentSettingValue>
  updated_at: string | null
  updated_by: string | null
  /** PUT only (never present on GET) — the keys this call actually changed; `[]` on a no-op patch. */
  changed_keys?: string[]
}

/** GET/PUT .../agent-settings response — the standard {success, data} envelope, like ClusterResponse. */
export interface AgentSettingsResponse {
  success: boolean
  data: AgentSettingsData
}

/** PUT body — a partial merge patch keyed by descriptor `key`, e.g. `{"a2a": {"enabled": true}}`. */
export type AgentSettingsUpdateRequest = Record<string, Partial<AgentSettingValue>>

/** Kubernetes cpu/memory quantities. A missing field means "use the platform default". */
export interface AirflowResourceList {
  cpu?: string
  memory?: string
}
export interface AirflowComponentResources {
  requests?: AirflowResourceList
  limits?: AirflowResourceList
}
export type AirflowResourceComponent = 'api_server' | 'scheduler' | 'triggerer' | 'workers' | 'dag_processor'
/** engine.AirflowResources: optional per-component sizing; unset fields use the defaults. */
export type AirflowResources = Partial<Record<AirflowResourceComponent, AirflowComponentResources>> & {
  /** [api] workers of the api-server (AIRFLOW__API__WORKERS); 0 = default (1); omitted = keep the stored value. */
  api_server_workers?: number
}

// Airflow engine scaling/parallelism specification
export interface AirflowEngineSpec {
  /** Per-component requests/limits (engine settings > Airflow > Resources). Omitted = defaults. */
  resources?: AirflowResources
  /**
   * ADR-272: opt in to migrating a legacy install to a dedicated non-superuser
   * database role with random credentials. Restarts the Airflow components.
   */
  metadata_credential_migration_enabled?: boolean
  /**
   * ADR-272 Addendum 5: run KubernetesExecutor task pods in their own locked-down namespace with no
   * database, Fernet, JWT or API secrets. Effective only with Git Sync DAG delivery.
   */
  task_namespace_isolation?: boolean
  /** Bumped by "Rotate Airflow control secrets"; the agent applies the rotation once per generation. */
  control_credential_rotation_generation?: number
  /** Optional placement of the isolated task pods (dedicated node pool / sandbox RuntimeClass). */
  task_pod_runtime_class_name?: string
  task_pod_node_selector?: Record<string, string>
  task_pod_tolerations?: Array<{ key?: string; operator?: string; value?: string; effect?: string }>
  /** Seconds to trust a NetworkPolicy enforcement probe of the task namespace (0 = 24h, <0 = off). */
  network_policy_probe_interval_seconds?: number
  network_policy_probe_image?: string
  task_namespace_egress_ports?: number[]
  task_namespace_egress_except_cidrs?: string[]
  network_policy_require_enforced?: boolean
  /** Hold the deploy (no Helm change) while task isolation cannot take effect, instead of the shared layout. */
  require_task_isolation?: boolean
  /** Spec-side value of AirflowTaskPlacement.helm_reconcile_interval_seconds. */
  helm_reconcile_interval_seconds?: number
  control_credential_backup_retain?: number
  network_policy_probe_timeout_seconds?: number
  network_policy_probe_unenforced_recheck_seconds?: number
  network_policy_probe_retry_seconds?: number
  control_credential_rotation_backoff_seconds?: number
  control_credential_backup_max_age_seconds?: number
  control_credential_drain_timeout_seconds?: number
  // Scheduler
  scheduler_replicas: number
  // API Server
  webserver_replicas: number
  api_server_hpa_enabled: boolean
  api_server_min_replicas: number
  api_server_max_replicas: number
  api_server_hpa_cpu_target: number   // 0 = use default (60)
  // Triggerer
  triggerer_replicas: number
  triggerer_hpa_enabled: boolean
  triggerer_min_replicas: number
  triggerer_max_replicas: number
  triggerer_hpa_cpu_target: number    // 0 = use default (50)
  // Parallelism
  parallelism: number                  // 0 = default (32)
  max_active_runs_per_dag: number      // 0 = default (16)
  max_active_tasks_per_dag: number     // 0 = default (16)
  // Storage
  postgresql_storage_gb: number        // 0 = default (50)
  // Passthrough fields — not shown in the engine config UI but must round-trip
  // through save to avoid silently clearing git-sync or external-DB configuration.
  chart_version?: string
  airflow_version?: string
  executor_type?: string
  worker_replicas?: number
  min_worker_replicas?: number
  max_worker_replicas?: number
  dag_git_repo?: string
  dag_git_branch?: string
  dag_git_sync_period?: string
  dag_git_ssh_secret_name?: string
  database_url?: string
  /**
   * INERT — round-tripped only so save does not clear it. Nothing deploys into
   * this namespace: the agent's AirflowEngineDeployer.resolveNamespace ignores
   * the spec and always uses the workloads namespace fixed at BYOK onboarding
   * (that is `Cluster.namespace`, the "Namespace" field in CreateClusterModal).
   * Never surface this as an editable field — it would accept a value, store
   * it, echo it back, and change nothing.
   */
  namespace?: string
  tier?: string
  drain_timeout_seconds?: number
}

// Dagster engine scaling specification
export interface DagsterEngineSpec {
  webserver_replicas: number
  daemon_replicas: number              // always 1, read-only
  user_code_replicas: number
  // Webserver HPA
  webserver_hpa_enabled: boolean
  webserver_min_replicas: number
  webserver_max_replicas: number
  webserver_hpa_cpu_target: number     // 0 = use default (60)
  // Run queue
  max_concurrent_runs: number          // 0 = default (10)
  // Storage
  postgresql_storage_gb: number        // 0 = default (50)
}

// Airflow provisioning configuration (sent on first enable)
// Update cluster request
/** Optional Airflow task pod placement and NetworkPolicy probe settings; replaced as one unit. */
export interface AirflowTaskPlacement {
  runtime_class_name?: string
  node_selector?: Record<string, string>
  tolerations?: Array<{ key?: string; operator?: string; value?: string; effect?: string }>
  network_policy_probe_interval_seconds?: number
  network_policy_probe_image?: string
  network_policy_probe_timeout_seconds?: number
  network_policy_probe_unenforced_recheck_seconds?: number
  network_policy_probe_retry_seconds?: number
  control_credential_rotation_backoff_seconds?: number
  control_credential_backup_retain?: number
  control_credential_backup_max_age_seconds?: number
  control_credential_drain_timeout_seconds?: number
  /** External TCP ports the isolated task namespace may reach (default [443]; BYOK perimeters allow 443 only). */
  task_namespace_egress_ports?: number[]
  task_namespace_egress_except_cidrs?: string[]
  /** Refuse the move to the isolated namespace while NetworkPolicy is not enforced or unverified. */
  network_policy_require_enforced?: boolean
  /** Hold the Airflow deploy (no Helm change) while task isolation cannot take effect (default: shared layout + finding). */
  require_task_isolation?: boolean
  /** Seconds before an unchanged release is re-applied to repair drift: 0 = default (24h), -1 = never, else 3600..7776000. */
  helm_reconcile_interval_seconds?: number
}

/** Dedicated Airflow security controls of PUT cluster (absent fields change nothing; the server checks the role). */
export interface AirflowControlsPatch {
  airflow_task_isolation?: boolean
  rotate_airflow_control_secrets?: boolean
  airflow_metadata_credential_migration?: boolean
  airflow_task_placement?: AirflowTaskPlacement
}

export interface UpdateClusterRequest extends AirflowControlsPatch {
  display_name?: string
  resources?: ClusterResources
  engines?: EngineType[]
  queue_config?: QueueConfig
  platform_services?: PlatformServicesConfig
  monitoring_config?: MonitoringConfig
  console_url?: string
  langflow_console_url?: string
  airflow_console_url?: string
  dagster_console_url?: string
  delivery_preference?: string
  poll_interval_seconds?: number
  /**
   * P2.77 — top-level convenience field mirroring `delivery_preference`/
   * `poll_interval_seconds` above: the Settings tab sends only the changed
   * directive, and the backend merges it into `queue_config` server-side
   * rather than requiring the full `queue_config` object on every save. See
   * `QueueConfig.job_output_sink_enabled` for the persisted shape.
   */
  job_output_sink_enabled?: boolean
  /** Sets only the Airflow DAG Git repo and branch, keeping the rest of airflow_spec. */
  airflow_git_sync?: { dag_git_repo: string; dag_git_branch: string }
  airflow_spec?: AirflowEngineSpec
  dagster_spec?: DagsterEngineSpec
}

// List response with pagination
export interface ClustersResponse {
  success: boolean
  data: Cluster[]
  meta?: {
    total: number
    limit: number
    offset: number
  }
}

// Single cluster response
export interface ClusterResponse {
  success: boolean
  data: Cluster
}

// Agents response
export interface AgentsResponse {
  success: boolean
  data: Agent[]
}

// Events response
export interface EventsResponse {
  success: boolean
  data: ClusterEvent[]
}

// Token rotation request — ttl_minutes is optional; backend defaults to 1440 (24h)
export interface TokenRotateRequest {
  ttl_minutes?: number
}

// Token rotation response
export interface TokenRotateResponse {
  success: boolean
  data: {
    token: string
    expires_at: string
    ttl_minutes?: number
    /** Address the agent must call; absent on older gateways. */
    platform_url?: string
    /** The served chart's signing state (round 6, D1): with it the install command verifies the chart first. */
    chart_trust?: ChartTrust
  }
}

// Health response
export interface HealthResponse {
  success: boolean
  data: ClusterHealth
}

// Console launch response
export interface ConsoleLaunchResponse {
  success: boolean
  data: {
    launch_url: string
    expires_in: number
  }
}

// Assigned managed cluster (for tenant view)
export interface AssignedManagedCluster {
  id: string
  name: string
  display_name?: string
  deployment_mode: DeploymentMode
  region?: string
  tier?: string
  engines: EngineType[]
  status: ClusterStatus
  is_default: boolean
  assigned_at?: string
}

// Assigned managed clusters response
export interface AssignedManagedClustersResponse {
  success: boolean
  data: AssignedManagedCluster[]
}

// A workflow-to-cluster (or tenant-default) routing assignment. Go:
// internal/domain/cluster.ClusterAssignment (internal/domain/cluster/entity.go:596).
export interface ClusterAssignment {
  id: string
  tenant_id: string
  cluster_id: string
  workflow_id?: string
  priority: number
  is_default: boolean
  created_at: string
  updated_at: string
}

export interface ClusterAssignmentsResponse {
  success: boolean
  data: ClusterAssignment[]
  meta?: { total: number; limit: number; offset: number }
}

// Cluster API
export const clusterApi = {
  /**
   * Get all clusters for the current tenant (BYOK clusters)
   */
  getAll: (limit = 20, offset = 0, status?: ClusterStatus, includeDeleted = false, archived = false, signal?: AbortSignal) => {
    let url = `/tenant/clusters?limit=${limit}&offset=${offset}`
    if (status) {
      url += `&status=${status}`
    }
    if (includeDeleted) {
      url += `&include_deleted=true`
    }
    if (archived) {
      url += `&archived=true`
    }
    return apiClient.get<ClustersResponse>(url, undefined, signal ? { signal } : undefined)
  },

  /**
   * Get assigned managed clusters for the current tenant (read-only view)
   */
  getAssignedManagedClusters: () =>
    apiClient.get<AssignedManagedClustersResponse>('/tenant/clusters/assigned'),

  /**
   * Get a single cluster by ID
   */
  getById: (id: string, signal?: AbortSignal) =>
    apiClient.get<ClusterResponse>(`/tenant/clusters/${id}`, undefined, signal ? { signal } : undefined),

  /**
   * Tenant-wide agent upgrade summary (Phase 1): how many of the tenant's BYOK
   * clusters have an agent below the platform minimum. Counted server-side over
   * every cluster, not one list page (internal/api/v1/cluster/agent_upgrade_status.go).
   */
  getAgentUpgradeSummary: () =>
    apiClient.get<{ success: boolean; data: AgentUpgradeSummary }>('/tenant/clusters/agent-upgrade-summary'),

  /**
   * One-click agent upgrade (ADR-303): what the console may offer for ONE cluster and the state of its latest request.
   */
  getAgentUpgrade: (clusterId: string) =>
    apiClient.get<{ success: boolean; data: AgentOneClickView }>(`/tenant/clusters/${clusterId}/agent-upgrade`),

  /**
   * Request an upgrade (or the vector-store feature switch) for ONE cluster. Owner/admin only; the server re-checks the live role.
   */
  requestAgentUpgrade: (clusterId: string, body: AgentUpgradeRequestBody) =>
    apiClient.post<{ success: boolean; data: AgentUpgradeRequestRecord }>(`/tenant/clusters/${clusterId}/agent-upgrade`, body),

  /**
   * Create a new cluster
   */
  create: (data: CreateClusterRequest) =>
    apiClient.post<CreateClusterResponse>('/tenant/clusters', data),

  /**
   * Update an existing cluster
   */
  update: (id: string, data: UpdateClusterRequest) =>
    apiClient.put<ClusterResponse>(`/tenant/clusters/${id}`, data),

  /**
   * Delete a cluster
   */
  delete: (id: string, opts?: { acknowledgeImpact?: boolean }) =>
    apiClient.delete<{ success: boolean; data: { message: string } }>(
      `/tenant/clusters/${id}${opts?.acknowledgeImpact ? '?acknowledge_impact=true' : ''}`,
    ),

  /**
   * Update cluster status
   */
  updateStatus: (id: string, status: ClusterStatus, reason?: string) =>
    apiClient.put<ClusterResponse>(`/tenant/clusters/${id}/status`, { status, reason }),

  /**
   * Get the default cluster for the tenant.
   *
   * NOT the same thing as the tenant's default CLUSTER ASSIGNMENT (see
   * listAssignments below) — this returns "the first active BYOK cluster"
   * (internal/api/v1/cluster/handler.go GetDefault), unrelated to
   * cluster_assignments.is_default. Agentic chat/run dispatch resolves the
   * target cluster from the assignment (internal/domain/ai/
   * cluster_resolver_impl.go), never from this endpoint — do not use this
   * for "where will this run" displays.
   */
  getDefault: () => apiClient.get<ClusterResponse>('/tenant/clusters/default'),

  /**
   * List the tenant's cluster assignments (workflow-specific overrides plus
   * the tenant default, `is_default: true`). Chat/agentic dispatch only ever
   * consults the DEFAULT assignment — ClusterResolverImpl.
   * ResolveClusterForTenant (internal/domain/ai/cluster_resolver_impl.go:106)
   * never looks up a workflow-specific one for chat jobs — so callers
   * resolving "where will this chat run" should filter for `is_default`,
   * not the caller's own workflow id.
   */
  listAssignments: () => apiClient.get<ClusterAssignmentsResponse>('/tenant/cluster/assignments'),

  /**
   * Get agents for a cluster
   */
  getAgents: (clusterId: string) =>
    apiClient.get<AgentsResponse>(`/tenant/clusters/${clusterId}/agents`),

  /**
   * Get events for a cluster
   */
  getEvents: (clusterId: string, limit = 50) =>
    apiClient.get<EventsResponse>(`/tenant/clusters/${clusterId}/events?limit=${limit}`),

  /**
   * Rotate agent token
   * @param clusterId - The cluster ID
   * @param request - Optional TTL in minutes (15–43200). Backend defaults to 1440 (24h).
   */
  rotateToken: (clusterId: string, request: TokenRotateRequest = {}) =>
    apiClient.post<TokenRotateResponse>(`/tenant/clusters/${clusterId}/token/rotate`, request),

  /**
   * Get cluster health summary
   */
  getHealth: (clusterId: string) =>
    apiClient.get<HealthResponse>(`/tenant/clusters/${clusterId}/health`),

  /**
   * Launch engine console (SSO) — supports langflow and airflow
   */
  consoleLaunch: (clusterId: string, engine: EngineType = 'langflow') =>
    apiClient.post<ConsoleLaunchResponse>(`/tenant/clusters/${clusterId}/console-launch?engine=${engine}`, {}), // supports langflow, airflow

  /**
   * Force delete a cluster that is stuck in deleting state with offline agent
   */
  forceDelete: (clusterId: string) =>
    apiClient.post<{ success: boolean; data: { message: string } }>(`/tenant/clusters/${clusterId}/force-delete`, {}),

  /**
   * Archive a BYOK cluster -- a reversible "Parked" state. Excluded from
   * default lists/pickers/dispatch until unarchived; the agent stays
   * connected and cost metering keeps running. Tenant-admin only,
   * platform-managed clusters are rejected (403).
   */
  archive: (clusterId: string) =>
    apiClient.post<ClusterResponse>(`/tenant/clusters/${clusterId}/archive`, {}),

  /**
   * Unarchive a BYOK cluster -- clears archived_at/archived_by and restores
   * default visibility/dispatch eligibility.
   */
  unarchive: (clusterId: string) =>
    apiClient.post<ClusterResponse>(`/tenant/clusters/${clusterId}/unarchive`, {}),

  /**
   * H19 — get this cluster's generic agent-settings descriptors + desired
   * values (A2A is the first descriptor; see AgentSettingDescriptor).
   */
  getAgentSettings: (clusterId: string) =>
    apiClient.get<AgentSettingsResponse>(`/tenant/clusters/${clusterId}/agent-settings`),

  /**
   * H19 — partial-merge patch of desired agent-settings values, e.g.
   * `{"a2a": {"enabled": true}}`. The platform delivers the merged desired
   * state (plus any secret material a descriptor requires — the UI never
   * sees it) to the agent on its next heartbeat. Per-row role gating is
   * server-enforced too (403 for a role below the descriptor's role_tier).
   */
  updateAgentSettings: (clusterId: string, patch: AgentSettingsUpdateRequest) =>
    apiClient.put<AgentSettingsResponse>(`/tenant/clusters/${clusterId}/agent-settings`, patch),

  /** Tenant default of the Airflow task-log archive setting (Archival page). */
  getTaskLogArchiveDefault: () =>
    apiClient.get<{ success: boolean; data: TaskLogArchiveDefault }>('/tenant/clusters/task-log-archive-default'),

  setTaskLogArchiveDefault: (enabled: boolean) =>
    apiClient.put<{ success: boolean; data: TaskLogArchiveDefault }>('/tenant/clusters/task-log-archive-default', { enabled }),

  /** Copies the tenant default onto every cluster (per-cluster result list). */
  applyTaskLogArchiveDefault: () =>
    apiClient.post<{ success: boolean; data: TaskLogArchiveApplyData }>('/tenant/clusters/task-log-archive-default/apply', {}),

  /**
   * The tenant's CPU/memory use over its BYOK clusters and the managed clusters assigned to it
   * (percentages and counts only). Feeds the Home cluster-usage widgets.
   */
  getResourceUsage: () =>
    apiClient.get<{ success: boolean; data: TenantResourceUsage }>('/tenant/clusters/resource-usage'),

  /**
   * Get live Kubernetes workload status for engine pods and HPAs (BYOK clusters).
   */
  getWorkloadStatus: (clusterId: string) =>
    apiClient.get<ClusterWorkloadStatusResponse>(`/tenant/clusters/${clusterId}/workloads`),

  /**
   * Get connectivity status for a BYOK cluster (agent, Kafka, Redis, worker readiness).
   */
  getConnectivityStatus: async (clusterId: string): Promise<ConnectivityStatus> => {
    const res = await apiClient.get<{ success: boolean; data: ConnectivityStatus }>(`/tenant/clusters/${clusterId}/connectivity-status`)
    return res.data
  },

  /**
   * Fetch the updated agent RBAC manifest (YAML) so a tenant admin can copy
   * or download it and `kubectl apply -f` it themselves — Vharta is never
   * granted RBAC-granting permissions on a BYOK cluster, so this can never
   * be a one-click "apply" from the platform (P2.25).
   */
  getAgentManifest: (clusterId: string): Promise<string> =>
    apiClient.get<string>(`/tenant/clusters/${clusterId}/agent-manifest`, undefined, { responseType: 'text' }),

  downloadCACert: async (clusterId: string): Promise<void> => {
    // Use the authenticated api client so the request carries the JWT.
    // window.open would bypass auth headers and receive a 401.
    // responseType: 'blob' makes apiClient.get() resolve the Blob directly.
    const blob = await apiClient.get<Blob>(`/tenant/clusters/${clusterId}/ca-cert`, undefined, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(blob as unknown as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'vharta-byok-ca.pem'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  },
}

// Deployment mode display names
export const deploymentModeLabels: Record<DeploymentMode, string> = {
  platform_managed_shared: 'Platform Managed (Shared)',
  platform_managed_dedicated: 'Platform Managed (Dedicated)',
  byok: 'Bring Your Own Kubernetes',
}

// Status display configuration
export const statusConfig: Record<
  ClusterStatus,
  { label: string; color: string; bgColor: string }
> = {
  requested: { label: 'Requested', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  provisioning: { label: 'Provisioning', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/40' },
  pending_agent: { label: 'Pending Agent', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-900/40' },
  agent_connecting: { label: 'Agent Connecting', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/40' },
  active: { label: 'Active', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/40' },
  degraded: { label: 'Degraded', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/40' },
  suspended: { label: 'Suspended', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  agent_offline: { label: 'Agent Offline', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/40' },
  deleting: { label: 'Deleting', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/40' },
  deleted: { label: 'Deleted', color: 'text-gray-400', bgColor: 'bg-gray-50' },
  failed: { label: 'Failed', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/40' },
}

/**
 * P2.489: the exact status_reason HeartbeatReconciler persists when it
 * transitions a cluster active->degraded because the agent reported the
 * cluster's own K8s apiserver unreachable (see
 * StatusReasonControlPlaneUnreachable in
 * internal/domain/cluster/heartbeat_reconciler.go). Matched exactly (not a
 * substring/humanize heuristic) so an unrelated future degraded reason is
 * never misrepresented as this one.
 */
const STATUS_REASON_CONTROL_PLANE_UNREACHABLE = 'control plane unreachable (reported by agent)'

/**
 * Returns the status pill label for a cluster, enriching the plain
 * statusConfig label with the specific reason for the one degraded cause
 * this platform currently recognizes (control-plane unreachable) — see
 * STATUS_REASON_CONTROL_PLANE_UNREACHABLE. Any other status, or a degraded
 * cluster with a different/absent status_reason, falls back to the plain
 * label unchanged. Shared by ClusterList's card and ClusterDetail so the two
 * can never drift on how this state reads.
 *
 * Note (P2.489 review): this reads the PERSISTED cluster.status_reason,
 * while ClusterHealthCard's badge reads the live GetHealth `issues` list
 * (an online agent's latest heartbeat). The two are populated by different
 * paths on different cadences (a reconciler tick vs. a live poll) and can
 * legitimately disagree for a heartbeat or two around a transition — e.g.
 * this label can still say plain "Degraded" for a moment after the health
 * panel already shows the enriched reason, or vice versa. Neither is wrong;
 * they converge within one reconcile interval.
 */
export function clusterStatusLabel(cluster: Pick<Cluster, 'status' | 'status_reason'>): string {
  const base = statusConfig[cluster.status].label
  if (cluster.status === 'degraded' && cluster.status_reason === STATUS_REASON_CONTROL_PLANE_UNREACHABLE) {
    return `${base} — control plane unreachable`
  }
  return base
}

// Agent status display configuration
export const agentStatusConfig: Record<
  AgentStatus,
  { label: string; color: string; bgColor: string }
> = {
  pending: { label: 'Pending', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  connecting: { label: 'Connecting', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/40' },
  connected: { label: 'Connected', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/40' },
  disconnected: { label: 'Disconnected', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-900/40' },
  unhealthy: { label: 'Unhealthy', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/40' },
  error: { label: 'Error', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/40' },
  decommissioned: { label: 'Decommissioned', color: 'text-gray-400', bgColor: 'bg-gray-50' },
}

/**
 * What deleting a cluster would break beyond the cluster itself — the body of
 * the 409 DELETE /tenant/clusters/{id} returns (code
 * `cluster_delete_needs_acknowledgement`) until the admin confirms with
 * `acknowledgeImpact`. See internal/domain/cluster/delete_safeguards.go.
 */
export interface ClusterDeleteImpact {
  /** Runs on this cluster that have not finished (they would be stopped). */
  active_executions: number
  /** This is the tenant's default cluster. */
  is_default: boolean
  /**
   * MCP connections explicitly bound to this cluster (target_cluster_id).
   * Deleting the cluster does not clear the binding, so each one silently
   * stops dispatching until it is pointed at another cluster — and
   * re-pointing one re-holds its approved tools for two-person review.
   */
  bound_mcp_connections: number
}

export const CLUSTER_DELETE_NEEDS_ACK_CODE = 'cluster_delete_needs_acknowledgement'

/** The impact from a delete that was refused pending acknowledgement, else null. */
export function deleteImpactFromError(err: unknown): ClusterDeleteImpact | null {
  if (!isApiError(err) || err.status !== 409 || err.code !== CLUSTER_DELETE_NEEDS_ACK_CODE) return null
  const data = (err.data ?? {}) as Partial<ClusterDeleteImpact>
  return {
    active_executions: Number(data.active_executions) || 0,
    is_default: data.is_default === true,
    // An older gateway omits the field entirely; 0 reads as "nothing bound"
    // rather than NaN, so the dialog simply drops that line.
    bound_mcp_connections: Number(data.bound_mcp_connections) || 0,
  }
}

/**
 * The code on the 409 `Delete`/`ForceDelete` return when the tenant has an
 * active legal hold (ADR-270 phase 0a). See
 * internal/domain/cluster/delete_safeguards.go's ErrClusterDeleteBlockedLegalHold.
 * Unlike `CLUSTER_DELETE_NEEDS_ACK_CODE`, there is no acknowledge-and-retry
 * for this one — the caller must resolve the hold externally first.
 */
export const CLUSTER_DELETE_LEGAL_HOLD_CODE = 'legal_hold_active'

/** True if the delete/force-delete was refused because of an active legal hold (or the hold check itself failed — both fail closed identically, same 409 + code). */
export function isLegalHoldBlockedError(err: unknown): boolean {
  return isApiError(err) && err.status === 409 && err.code === CLUSTER_DELETE_LEGAL_HOLD_CODE
}
